import React, { useState, useEffect } from 'react';
import { MonitorDot, BarChart3, Megaphone, HeadphonesIcon } from 'lucide-react';

function Benefits() {
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );

    const currentElement = document.getElementById('benefits-section');
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  // Responsive font sizes
  const getFontSize = (baseSize) => {
    if (windowWidth <= 480) return baseSize * 0.8;
    if (windowWidth <= 768) return baseSize * 0.9;
    return baseSize;
  };

  const styles = {
    container: {
      padding: windowWidth <= 768 ? '40px 16px' : '60px 16px',
      maxWidth: '1200px',
      margin: '0 auto',
      boxSizing: 'border-box',
      minHeight: windowWidth <= 768 ? 'auto' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
    },
    title: {
      fontSize: `${getFontSize(50)}px`,
      fontWeight: '600',
      marginBottom: windowWidth <= 480 ? '16px' : '24px',
      fontFamily: 'Oswald, sans-serif',
      color: '#410684',
      textAlign: 'center',
      lineHeight: windowWidth <= 480 ? '1.2' : '1.4',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.2s'
    },
    subtitle: {
      fontSize: `${getFontSize(16)}px`,
      color: '#414042',
      fontWeight: '400',
      fontFamily: 'Inter, sans-serif',
      lineHeight: '1.5',
      maxWidth: '910px',
      margin: '0 auto 24px',
      textAlign: 'center',
      padding: '0 12px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.4s'
    },
    benefitsRow: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100%',
      gap: windowWidth <= 768 ? '16px' : '24px'
    },
    row: {
      display: 'flex',
      width: '100%',
      gap: windowWidth <= 768 ? '16px' : '24px',
      flexDirection: windowWidth <= 768 ? 'column' : 'row'
    },
    benefitItem: (index) => ({
      flex: 1,
      display: 'flex',
      alignItems: windowWidth <= 480 ? 'center' : 'flex-start',
      flexDirection: windowWidth <= 480 ? 'column' : 'row',
      gap: '16px',
      padding: windowWidth <= 480 ? '16px' : '24px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease-out',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      transitionDelay: `${0.6 + index * 0.2}s`,
    }),
    iconContainer: {
      width: windowWidth <= 480 ? '40px' : '48px',
      height: windowWidth <= 480 ? '40px' : '48px',
      backgroundColor: '#410684',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'background-color 0.3s ease, transform 0.3s ease',
    },
    benefitContent: {
      flex: 1,
      textAlign: windowWidth <= 480 ? 'center' : 'left',
    },
    benefitTitle: {
      fontSize: `${getFontSize(20)}px`,
      fontWeight: '700',
      color: '#410684',
      marginBottom: '8px',
      lineHeight: '1.4',
      fontFamily: 'Oswald, sans-serif',
      letterSpacing: '0.5px'
    },
    benefitDescription: {
      fontSize: `${getFontSize(14)}px`,
      color: '#414042',
      fontWeight: '400',
      fontFamily: 'Inter, sans-serif',
      lineHeight: '1.5',
      margin: 0
    }
  };

  const benefits = [
    {
      icon: <MonitorDot size={windowWidth <= 480 ? 20 : 24} color="white" />,
      title: "FLEXIBLE SOLUTIONS",
      description: "Customizable and scalable solutions tailored to meet your unique business requirements."
    },
    {
      icon: <BarChart3 size={windowWidth <= 480 ? 20 : 24} color="white" />,
      title: "IT CONSULTANCY",
      description: "Expert guidance and strategic IT planning to optimize your technology infrastructure."
    },
    {
      icon: <Megaphone size={windowWidth <= 480 ? 20 : 24} color="white" />,
      title: "DIGITAL MARKETING",
      description: "Data-driven marketing strategies to enhance your online presence and drive results."
    },
    {
      icon: <HeadphonesIcon size={windowWidth <= 480 ? 20 : 24} color="white" />,
      title: "24/7 SUPPORT",
      description: "Round-the-clock technical support to ensure your operations run smoothly."
    }
  ];

  return (
    <div id="benefits-section" style={styles.container}>
      <h2 style={styles.title}>BENEFITS WITH OUR SERVICE</h2>
      <p style={styles.subtitle}>
        At Ejil Enterprise, we are dedicated to helping businesses streamline operations, reduce costs, and achieve growth.
      </p>
      
      <div style={styles.benefitsRow}>
        <div style={styles.row}>
          {benefits.slice(0, 2).map((benefit, index) => (
            <div key={index} style={styles.benefitItem(index)}>
              <div style={styles.iconContainer}>
                {benefit.icon}
              </div>
              <div style={styles.benefitContent}>
                <h3 style={styles.benefitTitle}>{benefit.title}</h3>
                <p style={styles.benefitDescription}>
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div style={styles.row}>
          {benefits.slice(2, 4).map((benefit, index) => (
            <div key={index + 2} style={styles.benefitItem(index + 2)}>
              <div style={styles.iconContainer}>
                {benefit.icon}
              </div>
              <div style={styles.benefitContent}>
                <h3 style={styles.benefitTitle}>{benefit.title}</h3>
                <p style={styles.benefitDescription}>
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Benefits;